import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, brl, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Plus, ArrowDownRight, ArrowUpRight, RotateCcw, Eye, Landmark, RefreshCw, Search, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { FornecedorPicker } from "@/components/FornecedorPicker";

const TIPOS = [
  { key: "entrada", label: "Entrada" },
  { key: "saida", label: "Saída" },
];
const ORIGENS = [
  { key: "manual", label: "Manual" },
  { key: "pagamento_reserva", label: "Pagamento de reserva" },
  { key: "conta_receber_recebimento", label: "Recebimento CR" },
  { key: "conta_pagar_pagamento", label: "Pagamento CP" },
  { key: "estorno", label: "Estorno" },
  { key: "outro", label: "Outro" },
];
const ORIGEM_LABEL = Object.fromEntries(ORIGENS.map((o) => [o.key, o.label]));
const STATUS = [
  { key: "efetivo", label: "Efetivo" },
  { key: "estornado", label: "Estornado" },
];

function today() { return new Date().toISOString().slice(0, 10); }
function firstDayOfMonth() {
  const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function lastDayOfMonth() {
  const d = new Date(); const l = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${l.getFullYear()}-${String(l.getMonth() + 1).padStart(2, "0")}-${String(l.getDate()).padStart(2, "0")}`;
}

const emptyForm = () => ({
  tipo: "entrada", valor: "", data_movimento: today(),
  descricao: "", conta_financeira_id: "", plano_de_contas_id: "",
  pousada_id: "", fornecedor: "", fornecedor_id: "", forma_pagamento: "", observacoes: "",
});

function StatusBadge({ status }) {
  const est = status === "estornado";
  return (
    <span
      data-testid={est ? "mf-status-estornado" : "mf-status-efetivo"}
      className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${
        est
          ? "bg-stone-100 text-stone-500 border border-stone-300"
          : "bg-[#EAF0EA] text-[#083A6B] border border-[#083A6B]/20"
      }`}
    >
      {est ? "Estornado" : "Efetivo"}
    </span>
  );
}

function TipoTag({ tipo }) {
  const isIn = tipo === "entrada";
  const Icon = isIn ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${isIn ? "text-[#083A6B]" : "text-[#D4A652]"}`}>
      <Icon size={14} strokeWidth={2} />
      {isIn ? "Entrada" : "Saída"}
    </span>
  );
}

export default function MovimentosFinanceiros() {
  const [items, setItems] = useState([]);
  const [contas, setContas] = useState([]);
  const [pousadas, setPousadas] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [me, setMe] = useState(null);

  const [loading, setLoading] = useState(true);
  const [erroCarga, setErroCarga] = useState(null);
  const [saldos, setSaldos] = useState({}); // cf_id -> {saldo, entradas, saidas}

  const [busca, setBusca] = useState("");
  const [filtros, setFiltros] = useState({
    de: firstDayOfMonth(), ate: lastDayOfMonth(),
    tipo: "all", conta_financeira_id: "all",
    pousada_id: "all", status: "all", origem: "all",
  });

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [detalheAlvo, setDetalheAlvo] = useState(null);
  const [detalheOpen, setDetalheOpen] = useState(false);
  const [detalheLoading, setDetalheLoading] = useState(false);
  const [auditoria, setAuditoria] = useState([]);

  const [estornoAlvo, setEstornoAlvo] = useState(null);
  const [estornoMotivo, setEstornoMotivo] = useState("");
  const [estornando, setEstornando] = useState(false);

  const podeEscrever = !!(me && (me.is_admin || me.is_finance));

  const load = useCallback(async () => {
    setLoading(true); setErroCarga(null);
    try {
      const params = {};
      if (filtros.de) params.de = filtros.de;
      if (filtros.ate) params.ate = filtros.ate;
      if (filtros.tipo !== "all") params.tipo = filtros.tipo;
      if (filtros.conta_financeira_id !== "all") params.conta_financeira_id = filtros.conta_financeira_id;
      if (filtros.pousada_id !== "all") params.pousada_id = filtros.pousada_id;
      if (filtros.status !== "all") params.status = filtros.status;
      if (filtros.origem !== "all") params.origem = filtros.origem;
      const { data } = await api.get("/movimentos-financeiros", { params });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setItems([]);
      setErroCarga(err?.response?.status === 401
        ? "Sessão expirada. Faça login novamente."
        : "Não foi possível carregar os movimentos.");
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  const loadRefs = useCallback(async () => {
    try {
      const [cf, pl, po, u] = await Promise.all([
        api.get("/contas-financeiras").catch(() => ({ data: [] })),
        api.get("/plano-contas").catch(() => ({ data: [] })),
        api.get("/pousadas-list").catch(() => ({ data: [] })),
        api.get("/auth/me").catch(() => ({ data: null })),
      ]);
      setContas(Array.isArray(cf.data) ? cf.data : []);
      // /plano-contas retorna árvore; achatar
      const flat = [];
      const walk = (nodes) => {
        (nodes || []).forEach((n) => {
          if (n.ativo !== false) flat.push(n);
          if (n.children?.length) walk(n.children);
        });
      };
      if (Array.isArray(pl.data)) walk(pl.data);
      setPlanos(flat);
      setPousadas(Array.isArray(po.data) ? po.data.filter((x) => x.id) : []);
      setMe(u.data);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => { loadRefs(); }, [loadRefs]);
  useEffect(() => { load(); }, [load]);

  // saldos derivados via F6 saldo — só se tiver ao menos 1 conta ativa
  useEffect(() => {
    (async () => {
      const map = {};
      for (const c of contas.filter((x) => x.ativo)) {
        try {
          const { data } = await api.get(`/movimentos-financeiros/saldo/${c.id}`);
          map[c.id] = data;
        } catch { /* ignore */ }
      }
      setSaldos(map);
    })();
  }, [contas]);

  const contaMap = useMemo(() => Object.fromEntries(contas.map((c) => [c.id, c])), [contas]);
  const pousadaMap = useMemo(() => Object.fromEntries(pousadas.map((p) => [p.id, p.nome])), [pousadas]);
  const planoMap = useMemo(() => Object.fromEntries(planos.map((p) => [p.id, p])), [planos]);

  const itensFiltrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return items;
    return items.filter((m) => {
      const alvo = [
        m.descricao, m.observacoes, m.fornecedor,
        contaMap[m.conta_financeira_id]?.nome,
        planoMap[m.plano_de_contas_id]?.nome,
        pousadaMap[m.pousada_id],
        m.origem,
      ].filter(Boolean).join(" ").toLowerCase();
      return alvo.includes(q);
    });
  }, [items, busca, contaMap, pousadaMap, planoMap]);

  const totais = useMemo(() => {
    let e = 0, s = 0;
    itensFiltrados.forEach((m) => {
      if (m.status !== "efetivo") return;
      const v = Number(m.valor || 0);
      if (m.tipo === "entrada") e += v; else if (m.tipo === "saida") s += v;
    });
    return { entradas: e, saidas: s, resultado: e - s };
  }, [itensFiltrados]);

  const abrirNovo = () => { setForm(emptyForm()); setFormOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.valor || Number(form.valor) <= 0) return toast.error("Valor deve ser maior que zero");
    if (!form.data_movimento) return toast.error("Informe a data do movimento");
    if (!form.conta_financeira_id) return toast.error("Selecione a conta financeira");
    setSaving(true);
    try {
      const payload = {
        tipo: form.tipo,
        valor: Number(form.valor),
        data_movimento: form.data_movimento,
        descricao: form.descricao.trim(),
        conta_financeira_id: form.conta_financeira_id,
        plano_de_contas_id: form.plano_de_contas_id || null,
        pousada_id: form.pousada_id || null,
        fornecedor: form.fornecedor.trim(),
        fornecedor_id: form.fornecedor_id || null,
        forma_pagamento: form.forma_pagamento.trim(),
        observacoes: form.observacoes.trim(),
      };
      await api.post("/movimentos-financeiros", payload);
      toast.success("Movimento registrado");
      setFormOpen(false);
      setForm(emptyForm());
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao registrar movimento";
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const abrirDetalhe = async (m) => {
    setDetalheAlvo(m); setDetalheOpen(true); setDetalheLoading(true); setAuditoria([]);
    try {
      const [{ data }, { data: aud }] = await Promise.all([
        api.get(`/movimentos-financeiros/${m.id}`),
        api.get(`/movimentos-financeiros/auditoria`, { params: { movimento_id: m.id, limit: 20 } }).catch(() => ({ data: [] })),
      ]);
      setDetalheAlvo(data);
      setAuditoria(Array.isArray(aud) ? aud : []);
    } catch { /* mantém básico */ }
    finally { setDetalheLoading(false); }
  };

  const confirmarEstorno = (m) => {
    if (m.status !== "efetivo") return toast.error("Este movimento já está estornado");
    if (m.estorna_movimento_id) return toast.error("Estornos não podem ser estornados");
    setEstornoAlvo(m); setEstornoMotivo("");
  };

  const executarEstorno = async () => {
    if (!estornoAlvo) return;
    if (!estornoMotivo.trim()) return toast.error("Informe o motivo do estorno");
    setEstornando(true);
    try {
      await api.post(`/movimentos-financeiros/${estornoAlvo.id}/estornar`, { motivo: estornoMotivo.trim() });
      toast.success("Movimento estornado");
      setEstornoAlvo(null); setEstornoMotivo("");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao estornar movimento";
      toast.error(String(msg));
    } finally {
      setEstornando(false);
    }
  };

  const contasAtivas = contas.filter((c) => c.ativo);
  const semContas = !loading && contas.length === 0;

  return (
    <div data-testid="page-movimentos-financeiros">
      <PageHeader
        eyebrow="Financeiro · Livro-Caixa"
        title="Movimentos Financeiros"
        description="Registre e consulte entradas e saídas efetivas por conta financeira. Movimentos não substituem pagamentos."
        actions={
          podeEscrever ? (
            <Dialog open={formOpen} onOpenChange={(v) => { setFormOpen(v); if (!v) setForm(emptyForm()); }}>
              <DialogTrigger asChild>
                <Button data-testid="btn-novo-movimento" onClick={abrirNovo} disabled={contasAtivas.length === 0}>
                  <Plus size={16} /> Novo movimento
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Novo movimento financeiro</DialogTitle>
                  <DialogDescription>
                    Registro manual no livro-caixa. Não cria pagamento, reserva, CP ou CR.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="mf-tipo">Tipo *</Label>
                    <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                      <SelectTrigger id="mf-tipo" data-testid="select-mf-tipo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TIPOS.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="mf-data">Data *</Label>
                    <Input id="mf-data" type="date" data-testid="input-mf-data" value={form.data_movimento}
                      onChange={(e) => setForm({ ...form, data_movimento: e.target.value })} required />
                  </div>
                  <div>
                    <Label htmlFor="mf-valor">Valor (BRL) *</Label>
                    <Input id="mf-valor" type="number" step="0.01" min="0.01"
                      data-testid="input-mf-valor" value={form.valor}
                      onChange={(e) => setForm({ ...form, valor: e.target.value })} required />
                  </div>
                  <div>
                    <Label htmlFor="mf-cf">Conta financeira *</Label>
                    <Select value={form.conta_financeira_id} onValueChange={(v) => setForm({ ...form, conta_financeira_id: v })}>
                      <SelectTrigger id="mf-cf" data-testid="select-mf-cf"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                      <SelectContent>
                        {contasAtivas.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="mf-desc">Descrição</Label>
                    <Input id="mf-desc" data-testid="input-mf-descricao" value={form.descricao}
                      onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                      placeholder="Ex.: Pagamento de energia elétrica" />
                  </div>
                  <div>
                    <Label htmlFor="mf-plano">Plano de contas</Label>
                    <Select value={form.plano_de_contas_id || "none"}
                      onValueChange={(v) => setForm({ ...form, plano_de_contas_id: v === "none" ? "" : v })}>
                      <SelectTrigger id="mf-plano" data-testid="select-mf-plano"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Sem plano</SelectItem>
                        {planos.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.codigo} · {p.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="mf-pousada">Pousada</Label>
                    <Select value={form.pousada_id || "none"}
                      onValueChange={(v) => setForm({ ...form, pousada_id: v === "none" ? "" : v })}>
                      <SelectTrigger id="mf-pousada" data-testid="select-mf-pousada"><SelectValue placeholder="Corporativo" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sem pousada / Corporativo</SelectItem>
                        {pousadas.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <FornecedorPicker
                      value={form.fornecedor_id}
                      valueTexto={form.fornecedor}
                      onSelect={(f) => setForm((prev) => ({
                        ...prev,
                        fornecedor_id: f?.id || "",
                        fornecedor: f?.razao_social_nome || prev.fornecedor,
                      }))}
                      onClear={() => setForm((prev) => ({ ...prev, fornecedor_id: "" }))}
                      onChangeTexto={(txt) => setForm((prev) => ({ ...prev, fornecedor: txt }))}
                      required={false}
                      inputTestId="input-mf-fornecedor"
                      pickerTestId="mf-fornecedor-picker"
                      labelText="Fornecedor"
                    />
                  </div>
                  <div>
                    <Label htmlFor="mf-forma">Forma de pagamento</Label>
                    <Input id="mf-forma" data-testid="input-mf-forma" value={form.forma_pagamento}
                      onChange={(e) => setForm({ ...form, forma_pagamento: e.target.value })}
                      placeholder="pix, dinheiro, transferência..." />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="mf-obs">Observações</Label>
                    <Input id="mf-obs" data-testid="input-mf-observacoes" value={form.observacoes}
                      onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
                  </div>
                  <DialogFooter className="md:col-span-2">
                    <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>Cancelar</Button>
                    <Button type="submit" data-testid="btn-salvar-mf" disabled={saving}>
                      {saving ? "Salvando..." : "Registrar movimento"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          ) : null
        }
      />

      {/* KPI saldos por conta (derivados via F6) */}
      {contasAtivas.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
          {contasAtivas.map((c) => {
            const s = saldos[c.id];
            return (
              <div key={c.id} data-testid={`mf-saldo-${c.id}`}
                className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-stone-500 mb-1">
                  <Landmark size={13} className="text-[#083A6B]" /> {c.nome}
                </div>
                <div className="font-serif text-2xl text-stone-900 tabular-nums">{brl(s?.saldo ?? c.saldo_inicial)}</div>
                {s && (
                  <div className="text-[11px] text-stone-500 mt-1">
                    entradas {brl(s.entradas)} · saídas {brl(s.saidas)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">De</Label>
          <Input type="date" data-testid="filter-mf-de" value={filtros.de}
            onChange={(e) => setFiltros({ ...filtros, de: e.target.value })} className="w-[150px]" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Até</Label>
          <Input type="date" data-testid="filter-mf-ate" value={filtros.ate}
            onChange={(e) => setFiltros({ ...filtros, ate: e.target.value })} className="w-[150px]" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Tipo</Label>
          <Select value={filtros.tipo} onValueChange={(v) => setFiltros({ ...filtros, tipo: v })}>
            <SelectTrigger data-testid="filter-mf-tipo" className="w-[130px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {TIPOS.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Conta</Label>
          <Select value={filtros.conta_financeira_id} onValueChange={(v) => setFiltros({ ...filtros, conta_financeira_id: v })}>
            <SelectTrigger data-testid="filter-mf-cf" className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {contas.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Pousada</Label>
          <Select value={filtros.pousada_id} onValueChange={(v) => setFiltros({ ...filtros, pousada_id: v })}>
            <SelectTrigger data-testid="filter-mf-pousada" className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {pousadas.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Status</Label>
          <Select value={filtros.status} onValueChange={(v) => setFiltros({ ...filtros, status: v })}>
            <SelectTrigger data-testid="filter-mf-status" className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {STATUS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Origem</Label>
          <Select value={filtros.origem} onValueChange={(v) => setFiltros({ ...filtros, origem: v })}>
            <SelectTrigger data-testid="filter-mf-origem" className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {ORIGENS.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Busca</Label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input value={busca} data-testid="filter-mf-busca"
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Descrição, fornecedor..." className="pl-8" />
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} aria-label="Atualizar" data-testid="btn-refresh-mf">
          <RefreshCw size={14} />
        </Button>
      </div>

      {/* Totais período */}
      <div className="flex flex-wrap gap-3 mb-4 text-sm">
        <div className="px-3 py-1.5 rounded-md bg-[#EAF0EA] text-[#083A6B] border border-[#083A6B]/20" data-testid="mf-total-entradas">
          Entradas efetivas: <b className="tabular-nums">{brl(totais.entradas)}</b>
        </div>
        <div className="px-3 py-1.5 rounded-md bg-[#F5E4DD] text-[#7c3a20] border border-[#D4A652]/25" data-testid="mf-total-saidas">
          Saídas efetivas: <b className="tabular-nums">{brl(totais.saidas)}</b>
        </div>
        <div className="px-3 py-1.5 rounded-md bg-stone-100 text-stone-800 border border-stone-200" data-testid="mf-total-resultado">
          Resultado do filtro: <b className="tabular-nums">{brl(totais.resultado)}</b>
        </div>
      </div>

      {/* Corpo */}
      {loading ? (
        <div data-testid="mf-loading" className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-10 text-center text-stone-500">
          Carregando movimentos...
        </div>
      ) : erroCarga ? (
        <div data-testid="mf-erro" className="bg-[#FFFFFF] border border-[#D4A652]/30 rounded-lg p-10 text-center text-[#D4A652]">
          {erroCarga}
        </div>
      ) : semContas ? (
        <div data-testid="mf-sem-contas" className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-10 text-center">
          <AlertTriangle size={22} className="mx-auto mb-3 text-[#D4A652]" />
          <div className="font-serif text-xl text-stone-900 mb-2">Nenhuma conta financeira cadastrada.</div>
          <p className="text-sm text-stone-600 mb-4">
            Movimentos financeiros exigem uma conta financeira. Cadastre a primeira conta para começar.
          </p>
          <Link to="/admin/financeiro/contas">
            <Button data-testid="btn-ir-contas"><Landmark size={14} /> Cadastrar conta financeira</Button>
          </Link>
        </div>
      ) : itensFiltrados.length === 0 ? (
        <div data-testid="mf-empty" className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-10 text-center">
          <div className="font-serif text-xl text-stone-900 mb-2">Nenhum movimento financeiro encontrado.</div>
          <p className="text-sm text-stone-600">
            Ajuste os filtros ou registre um novo movimento.
          </p>
        </div>
      ) : (
        <>
          {/* Mobile: cards */}
          <div className="md:hidden space-y-3">
            {itensFiltrados.map((m) => (
              <div key={m.id} data-testid={`mf-card-${m.id}`}
                className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div><TipoTag tipo={m.tipo} /></div>
                  <StatusBadge status={m.status} />
                </div>
                <div className="font-serif text-2xl text-stone-900 mb-1 tabular-nums">
                  {m.tipo === "saida" ? "− " : "+ "}{brl(m.valor)}
                </div>
                <div className="text-xs text-stone-600 space-y-0.5 mb-3">
                  <div><b>Data:</b> {fmtDate(m.data_movimento)}</div>
                  {m.descricao && <div className="truncate">{m.descricao}</div>}
                  <div><b>Conta:</b> {contaMap[m.conta_financeira_id]?.nome || "—"}</div>
                  {m.pousada_id && <div><b>Pousada:</b> {pousadaMap[m.pousada_id] || m.pousada_id}</div>}
                  <div><b>Origem:</b> {ORIGEM_LABEL[m.origem] || m.origem}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => abrirDetalhe(m)}
                    data-testid={`btn-mf-detalhe-${m.id}`}><Eye size={14} /> Detalhes</Button>
                  {podeEscrever && m.status === "efetivo" && !m.estorna_movimento_id && (
                    <Button size="sm" variant="outline" onClick={() => confirmarEstorno(m)}
                      data-testid={`btn-mf-estornar-${m.id}`}><RotateCcw size={14} /> Estornar</Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: tabela */}
          <div className="hidden md:block bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50 hover:bg-stone-50">
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Data</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Tipo</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Valor</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Descrição</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Conta</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Plano</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Pousada</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Origem</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itensFiltrados.map((m) => {
                  const p = planoMap[m.plano_de_contas_id];
                  return (
                    <TableRow key={m.id} data-testid={`mf-row-${m.id}`}>
                      <TableCell className="text-stone-700 text-sm whitespace-nowrap">{fmtDate(m.data_movimento)}</TableCell>
                      <TableCell><TipoTag tipo={m.tipo} /></TableCell>
                      <TableCell className={`text-right tabular-nums font-medium ${m.tipo === "saida" ? "text-[#D4A652]" : "text-[#083A6B]"}`}>
                        {m.tipo === "saida" ? "− " : "+ "}{brl(m.valor)}
                      </TableCell>
                      <TableCell className="text-stone-700 text-sm max-w-xs truncate">{m.descricao || "—"}</TableCell>
                      <TableCell className="text-stone-700 text-sm">{contaMap[m.conta_financeira_id]?.nome || "—"}</TableCell>
                      <TableCell className="text-stone-700 text-sm">{p ? `${p.codigo} ${p.nome}` : "—"}</TableCell>
                      <TableCell className="text-stone-700 text-sm">
                        {m.pousada_id ? (pousadaMap[m.pousada_id] || m.pousada_id) : <span className="text-stone-400">Corporativo</span>}
                      </TableCell>
                      <TableCell className="text-stone-700 text-sm">{ORIGEM_LABEL[m.origem] || m.origem}</TableCell>
                      <TableCell><StatusBadge status={m.status} /></TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => abrirDetalhe(m)}
                            aria-label="Ver detalhes" data-testid={`btn-mf-detalhe-${m.id}`}>
                            <Eye size={14} />
                          </Button>
                          {podeEscrever && m.status === "efetivo" && !m.estorna_movimento_id && (
                            <Button size="sm" variant="ghost" onClick={() => confirmarEstorno(m)}
                              aria-label="Estornar movimento" data-testid={`btn-mf-estornar-${m.id}`}>
                              <RotateCcw size={14} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {/* Detalhes */}
      <Dialog open={detalheOpen} onOpenChange={(v) => { setDetalheOpen(v); if (!v) { setDetalheAlvo(null); setAuditoria([]); } }}>
        <DialogContent className="max-w-xl" data-testid="dialog-mf-detalhe">
          <DialogHeader>
            <DialogTitle>Detalhes do movimento</DialogTitle>
            <DialogDescription>Informações completas conforme retorno do backend.</DialogDescription>
          </DialogHeader>
          {detalheLoading ? (
            <div className="text-center text-stone-500 py-6">Carregando...</div>
          ) : detalheAlvo ? (
            <>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div><div className="text-[11px] uppercase text-stone-500">ID</div><span className="font-mono text-xs">{detalheAlvo.id}</span></div>
                <div><div className="text-[11px] uppercase text-stone-500">Status</div><StatusBadge status={detalheAlvo.status} /></div>
                <div><div className="text-[11px] uppercase text-stone-500">Tipo</div><TipoTag tipo={detalheAlvo.tipo} /></div>
                <div><div className="text-[11px] uppercase text-stone-500">Valor</div><b className="tabular-nums">{brl(detalheAlvo.valor)}</b></div>
                <div><div className="text-[11px] uppercase text-stone-500">Data</div>{fmtDate(detalheAlvo.data_movimento)}</div>
                <div><div className="text-[11px] uppercase text-stone-500">Origem</div>{ORIGEM_LABEL[detalheAlvo.origem] || detalheAlvo.origem}</div>
                <div><div className="text-[11px] uppercase text-stone-500">Conta</div>{contaMap[detalheAlvo.conta_financeira_id]?.nome || "—"}</div>
                <div><div className="text-[11px] uppercase text-stone-500">Plano</div>
                  {planoMap[detalheAlvo.plano_de_contas_id]
                    ? `${planoMap[detalheAlvo.plano_de_contas_id].codigo} · ${planoMap[detalheAlvo.plano_de_contas_id].nome}`
                    : "—"}
                </div>
                <div><div className="text-[11px] uppercase text-stone-500">Pousada</div>
                  {detalheAlvo.pousada_id ? (pousadaMap[detalheAlvo.pousada_id] || detalheAlvo.pousada_id) : "Corporativo"}
                </div>
                <div><div className="text-[11px] uppercase text-stone-500">Forma pagamento</div>{detalheAlvo.forma_pagamento || "—"}</div>
                <div className="col-span-2"><div className="text-[11px] uppercase text-stone-500">Descrição</div>{detalheAlvo.descricao || "—"}</div>
                <div className="col-span-2"><div className="text-[11px] uppercase text-stone-500">Observações</div>{detalheAlvo.observacoes || "—"}</div>
                {detalheAlvo.estorna_movimento_id && (
                  <div className="col-span-2 text-[11px] text-stone-500">
                    Este é um <b>movimento de estorno</b> do original <span className="font-mono">{detalheAlvo.estorna_movimento_id.slice(0, 8)}…</span>
                  </div>
                )}
                {detalheAlvo.estornado_por_movimento_id && (
                  <div className="col-span-2 text-[11px] text-stone-500">
                    Estornado pelo movimento <span className="font-mono">{detalheAlvo.estornado_por_movimento_id.slice(0, 8)}…</span>
                  </div>
                )}
                <div className="col-span-2 text-[11px] text-stone-500 pt-2 border-t border-stone-200">
                  Criado em {detalheAlvo.created_at?.slice(0, 10) || "—"} · Atualizado em {detalheAlvo.updated_at?.slice(0, 10) || "—"}
                </div>
              </div>
              {auditoria.length > 0 && (
                <div className="mt-4 pt-4 border-t border-stone-200">
                  <div className="text-[11px] uppercase text-stone-500 mb-2">Auditoria</div>
                  <ul className="space-y-1 text-xs text-stone-600 max-h-40 overflow-y-auto">
                    {auditoria.map((a) => (
                      <li key={a.id}><b>{a.acao}</b> por {a.por} em {a.quando?.slice(0, 10)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Confirmação estorno */}
      <Dialog open={!!estornoAlvo} onOpenChange={(v) => { if (!v) { setEstornoAlvo(null); setEstornoMotivo(""); } }}>
        <DialogContent className="max-w-md" data-testid="dialog-mf-estorno">
          <DialogHeader>
            <DialogTitle>Estornar este movimento?</DialogTitle>
            <DialogDescription>
              O movimento original será preservado e o estorno será registrado conforme as regras financeiras do sistema.
            </DialogDescription>
          </DialogHeader>
          {estornoAlvo && (
            <div className="text-sm text-stone-700 space-y-2">
              <div><TipoTag tipo={estornoAlvo.tipo} /> · <b className="tabular-nums">{brl(estornoAlvo.valor)}</b> · {fmtDate(estornoAlvo.data_movimento)}</div>
              <div className="text-xs text-stone-500 truncate">{estornoAlvo.descricao || "—"}</div>
            </div>
          )}
          <div>
            <Label htmlFor="mf-estorno-motivo">Motivo *</Label>
            <Input id="mf-estorno-motivo" data-testid="input-mf-estorno-motivo"
              value={estornoMotivo} onChange={(e) => setEstornoMotivo(e.target.value)}
              placeholder="Descreva o motivo do estorno" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEstornoAlvo(null); setEstornoMotivo(""); }} disabled={estornando}>
              Cancelar
            </Button>
            <Button data-testid="btn-confirmar-estorno" onClick={executarEstorno} disabled={estornando || !estornoMotivo.trim()}>
              {estornando ? "Estornando..." : "Estornar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
